import React from 'react';
import {
  Document as PDFDocument,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from '@react-pdf/renderer';
import {
  InvoiceDetailData,
  InvoiceLineItem,
  computeLineItemTotals,
  formatSiteAddress,
  isProgressClaimProjectType,
  buildClientAddressLines,
  resolveCareOf,
  formatCurrency,
  formatInvoiceDate,
  hasGapBetween,
} from '../../config';
import { PDF_DOCUMENT_FONT } from '../../../../utils/pdfFonts';
import {
  FONT_SIZE_PT,
  LINE_HEIGHT,
  PAGE_MARGIN_TOP_MM,
  PAGE_MARGIN_RIGHT_MM,
  PAGE_MARGIN_BOTTOM_MM,
  PAGE_MARGIN_LEFT_MM,
  PAGE_FOOTER_BOTTOM_MM,
  PAGE_HEIGHT_MM,
  HEADER_BAND_PAD_MM,
  LINE_ITEM_LINE_PX,
  LINE_ITEM_FIRST_ROW_PAD_PX,
  DRAWING_MARK_PX,
  DRAWING_NUMBER_MM,
  DRAWING_ISSUE_MM,
  DRAWING_ISSUE_LABEL_PX,
  DRAWING_NUMBER_OVERFLOW_MM,
  LINE_ITEM_INDENT_PX,
  mmToPt,
  pxToPt,
  InvoicePagePlan,
} from '../../invoiceLayout';

/**
 * The editor's stylesheet is written in CSS px; this converts those to the
 * points react-pdf works in, so a value copied from the stylesheet lands at
 * the same physical size here. Values already in mm use mmToPt.
 */
const px = pxToPt;

// One blank line in the header's rhythm, matching the editor's
// $invoice-header-line: 12px type at the page's 1.4 leading. The header follows
// the firm's Word letter template - client block, one blank line, ATTENTION,
// two blank lines, the PROJECT block, one blank line, then the table.
const HEADER_FONT_PT = px(12);
const HEADER_LINE_PT = px(16.8);

/**
 * Pairs a font size with the page's leading.
 *
 * react-pdf resolves a unitless `lineHeight` against the `fontSize` declared in
 * the SAME style object, then inherits the resulting absolute value. A style
 * that only changes fontSize therefore keeps the page's line height in points
 * and silently sets its own text on the wrong leading - a 12px header line came
 * out 18.67px tall instead of 16.8px, and every such line drifted from the
 * editor a little further down the page. Browsers re-resolve the ratio at each
 * element, so the editor never had the problem.
 *
 * Every style below that sets a size does so through this, so the two can only
 * ever be declared together.
 */
const type = (sizePt: number) => ({ fontSize: sizePt, lineHeight: LINE_HEIGHT });

// Colours and the table type scale, taken from the editor stylesheet so the
// two surfaces cannot drift.
const COLOR_TEXT_PRIMARY = '#111827';   // $text-primary
const COLOR_TEXT_SECONDARY = '#6b7280'; // $text-secondary
const COLOR_GRAY_100 = '#f3f4f6';
const COLOR_GRAY_200 = '#e5e7eb';
const COLOR_DANGER = '#dc3545';         // $danger-color
const TABLE_FONT_PT = px(11);           // editor: .line-items-table font-size 11px

// react-pdf sizes a `wrap={false}` page to its content, so each page holds one
// fixed-height box and stays exactly A4.
const BODY_BOX_HEIGHT_PT =
  mmToPt(PAGE_HEIGHT_MM) - mmToPt(PAGE_MARGIN_TOP_MM) - mmToPt(PAGE_MARGIN_BOTTOM_MM);

const styles = StyleSheet.create({
  page: {
    paddingTop: mmToPt(PAGE_MARGIN_TOP_MM),
    paddingBottom: mmToPt(PAGE_MARGIN_BOTTOM_MM),
    paddingLeft: mmToPt(PAGE_MARGIN_LEFT_MM),
    paddingRight: mmToPt(PAGE_MARGIN_RIGHT_MM),
    fontSize: FONT_SIZE_PT,
    // Overridden per-render by the fontFamily prop when the Tinos faces fail
    // to load; nothing else in this stylesheet sets a family, so the whole
    // document follows the page.
    fontFamily: PDF_DOCUMENT_FONT,
    lineHeight: LINE_HEIGHT,
  },

  // Header band: pushed to top of page, logo left, TAX INVOICE right
  headerBand: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: mmToPt(-PAGE_MARGIN_TOP_MM),
    marginLeft: mmToPt(-PAGE_MARGIN_LEFT_MM),
    marginRight: mmToPt(-PAGE_MARGIN_RIGHT_MM),
    paddingLeft: mmToPt(PAGE_MARGIN_LEFT_MM),
    paddingRight: mmToPt(PAGE_MARGIN_RIGHT_MM),
    // editor: 8mm above and below the letterhead rule. This has to match, not
    // just look close: the band is pulled up by the full top margin, so any
    // shortfall here lifts the entire invoice body relative to the preview.
    paddingTop: mmToPt(HEADER_BAND_PAD_MM),
    paddingBottom: mmToPt(HEADER_BAND_PAD_MM),
    marginBottom: px(20), // editor: margin-bottom 20px
    borderBottomWidth: 1,
    borderBottomColor: COLOR_GRAY_200, // editor: $color-gray-200
    borderBottomStyle: 'solid',
  },
  pageBox: {
    height: BODY_BOX_HEIGHT_PT,
    flexDirection: 'column',
  },
  headerAccent: {
    position: 'absolute',
    right: 0,
    top: '25%',
    height: '50%',
    width: px(3),                       // editor: width 3px
    backgroundColor: '#B5C327',
  },
  logo: {
    width: px(188),                     // editor: width 188px
  },
  headerTitle: {
    ...type(px(18)),                   // editor: font-size 18px
    fontWeight: 'bold',
    color: '#1A2E5B',                   // $primary-dark
    letterSpacing: px(2),               // editor: letter-spacing 2px
  },

  // Two-column meta: client left, invoice details right
  metaSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: HEADER_LINE_PT,       // editor: margin-bottom $invoice-header-line
  },
  metaLeft: {
    flex: 1,
    paddingRight: px(40),               // editor: gap 40px
  },
  metaRight: {
    width: px(200),                     // editor: min-width 200px
  },
  // Client block: editor .invoice-preview__client-section. The lines sit on
  // natural leading with no gaps, as they do in the letter template.
  clientHeaderLine: {
    ...type(HEADER_FONT_PT),
  },
  clientName: {
    ...type(HEADER_FONT_PT),
    fontWeight: 'bold',
  },
  clientCareOf: {
    ...type(HEADER_FONT_PT),
  },
  // Client body lines (street / PO box / email) - pre-cased by the helper, so
  // rendered verbatim here (street is Title Cased, PO box / email as entered).
  clientAddress: {
    ...type(HEADER_FONT_PT),
  },
  clientCityLine: {
    ...type(HEADER_FONT_PT),
    textTransform: 'uppercase',
    fontWeight: 'bold',
  },
  metaRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: px(3),             // editor: padding 3px 0
    borderBottomWidth: px(1),
    borderBottomColor: COLOR_GRAY_100,
    borderBottomStyle: 'solid',
  },
  metaLabel: {
    ...type(px(11)),                   // editor: font-size 11px
    fontWeight: 'normal',               // editor: 500, resolves to the 400 face
  },
  metaValue: {
    ...type(px(11)),
    fontWeight: 'bold',                 // editor: 600, resolves to the 700 face
    textAlign: 'right',
  },

  // ATTENTION row
  attentionRow: {
    flexDirection: 'row',
    marginBottom: HEADER_LINE_PT * 2,   // editor: two blank lines, none above
    textTransform: 'uppercase',
    fontWeight: 'bold',
    ...type(HEADER_FONT_PT),
  },
  attentionLabel: {
    width: px(90),                      // editor: width 90px
  },

  // PROJECT row
  projectRow: {
    flexDirection: 'row',
    ...type(HEADER_FONT_PT),
  },
  projectLabel: {
    width: px(90),
    fontWeight: 'bold',
  },
  projectContent: {
    flex: 1,
    textTransform: 'uppercase',
    fontWeight: 'bold',
  },

  // Address (plain unlabeled line, aligned under the PROJECT description)
  addressRow: {
    ...type(HEADER_FONT_PT),
    paddingLeft: px(90),
    textTransform: 'uppercase',
    fontWeight: 'bold',
  },

  // Service description + PO (indented under PROJECT). These are .inline-edit
  // inputs on screen, but with no vertical padding, so they occupy exactly one
  // line box here just as they do there.
  serviceDesc: {
    ...type(HEADER_FONT_PT),
    textTransform: 'uppercase',
    paddingLeft: px(90),
  },
  poRow: {
    ...type(HEADER_FONT_PT),
    paddingLeft: px(90),
  },

  claimTitle: {
    ...type(px(12)),
    fontWeight: 'bold',                 // editor: 600, resolves to the 700 face
    marginTop: HEADER_LINE_PT,          // editor: margin-top $invoice-header-line
    marginBottom: px(6),                // editor: margin-bottom 6px
  },

  // Line items table
  tableHeader: {
    flexDirection: 'row',
    borderBottomWidth: px(2),           // editor: border-bottom 2px solid #000
    borderBottomColor: '#000000',
    borderBottomStyle: 'solid',
    paddingBottom: px(6),               // editor: th padding 6px 0
    paddingTop: px(6),
    // Air under the rule before the first row; the editor spends it as
    // padding-top on that row (its rows carry none of their own), which has no
    // equivalent here.
    marginBottom: px(LINE_ITEM_FIRST_ROW_PAD_PX),
  },
  tableHeaderCell: {
    ...type(px(9)),                    // editor: th font-size 9px
    fontWeight: 'bold',                 // editor: 600, resolves to the 700 face
    textTransform: 'uppercase',
    letterSpacing: px(9 * 0.05),        // editor: letter-spacing 0.05em
  },
  // Every line sits on its natural line height; separation is whole blank
  // lines, applied above a line when it and the one before it agree.
  // editor: .line-item-row td { padding: 0 }
  tableRow: {
    flexDirection: 'row',
  },
  // editor: .line-item-row--gap-above td { padding-top: $line-item-line }
  tableRowGapAbove: {
    flexDirection: 'row',
    paddingTop: px(LINE_ITEM_LINE_PX),
  },
  colDescription: {
    flex: 1,
    paddingRight: px(8),                // editor: padding-right 8px
  },
  colQty: {
    width: px(40),                      // editor: width 40px
    textAlign: 'right',
  },
  colRate: {
    width: px(65),                      // editor: width 65px
    textAlign: 'right',
  },
  colAmount: {
    width: px(80),                      // editor: width 80px
    textAlign: 'right',
  },
  cellText: {
    ...type(TABLE_FONT_PT),
    color: COLOR_TEXT_PRIMARY,
  },
  cellNumber: {
    ...type(TABLE_FONT_PT),
    color: COLOR_TEXT_PRIMARY,
  },
  textLine: {
    ...type(TABLE_FONT_PT),
    color: COLOR_TEXT_PRIMARY,
  },
  bulletLine: {
    ...type(TABLE_FONT_PT),
    color: COLOR_TEXT_PRIMARY,
  },
  bulletRow: {
    flexDirection: 'row',
  },
  bulletMark: {
    ...type(TABLE_FONT_PT),
    width: px(6 + 6),                   // editor: bullet + 6px gap
    color: COLOR_TEXT_PRIMARY,
  },
  bulletText: {
    ...type(TABLE_FONT_PT),
    flex: 1,
    color: COLOR_TEXT_PRIMARY,
  },
  // A drawing line's columns, at the editor's widths (invoiceLayout.ts): the
  // mark, the number, "Issue X", then the title, which wraps under itself.
  drawingMark: {
    ...type(TABLE_FONT_PT),
    width: px(DRAWING_MARK_PX),
    color: COLOR_TEXT_PRIMARY,
  },
  drawingNumber: {
    width: mmToPt(DRAWING_NUMBER_MM),
  },
  // A drawing number is never broken across lines. react-pdf splits
  // "5910-STD-RETAINING-WALL-005" into a run of digits and a run of letters,
  // and when the whole number is wider than its box its fallback line breaker
  // breaks at that seam and draws an extra hyphen ("5910--") -- however high
  // the hyphenation penalty, and whatever the hyphen character. The editor
  // wrapped it at every hyphen instead: 2 lines in the PDF against 4 on
  // screen, which moves the page break. So the text gets a box wider than any
  // number, never needs breaking, and a number too long for its column runs
  // on into the next one -- as the editor's nowrap cell does.
  drawingNumberText: {
    ...type(TABLE_FONT_PT),
    width: mmToPt(DRAWING_NUMBER_OVERFLOW_MM),
    color: COLOR_TEXT_PRIMARY,
  },
  // A row of its own: the fixed-width label, then the value, which wraps
  // under itself -- as the editor's issue cell does.
  drawingIssue: {
    flexDirection: 'row',
    width: mmToPt(DRAWING_ISSUE_MM),
  },
  drawingIssueLabel: {
    ...type(TABLE_FONT_PT),
    width: px(DRAWING_ISSUE_LABEL_PX),
    color: COLOR_TEXT_PRIMARY,
  },
  drawingIssueValue: {
    ...type(TABLE_FONT_PT),
    flex: 1,
    color: COLOR_TEXT_PRIMARY,
  },
  drawingTitle: {
    ...type(TABLE_FONT_PT),
    flex: 1,
    color: COLOR_TEXT_PRIMARY,
  },
  deductionText: {
    ...type(TABLE_FONT_PT),
    color: COLOR_DANGER,                // editor: $danger-color
  },
  deductionAmount: {
    ...type(TABLE_FONT_PT),
    color: COLOR_DANGER,
  },

  // Closing summary: payment details left, totals right
  summarySection: {
    borderTopWidth: px(2),              // editor: border-top 2px solid #000
    borderTopColor: '#000000',
    borderTopStyle: 'solid',
    paddingTop: px(10),                 // editor: padding-top 10px
    marginTop: px(10),                  // editor: margin-top 10px
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  totalsColumn: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: px(250),                 // editor: flex 0 0 250px
    paddingTop: px(2),                  // editor: padding-top 2px
  },
  totalsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: px(3),             // editor: padding 3px 0
  },
  totalsLabel: {
    ...type(TABLE_FONT_PT),
    color: COLOR_TEXT_PRIMARY,
  },
  totalsValue: {
    width: px(100),                     // editor: min-width 100px
    ...type(TABLE_FONT_PT),
    fontWeight: 'normal',               // editor: 500, resolves to the 400 face
    textAlign: 'right',
    color: COLOR_TEXT_PRIMARY,
  },
  totalRowFinal: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: px(3),
    paddingBottom: px(6),               // editor: padding-bottom 6px
    borderBottomWidth: px(2),           // editor: border-bottom 2px solid #000
    borderBottomColor: '#000000',
    borderBottomStyle: 'solid',
  },
  totalLabelFinal: {
    ...type(px(12)),                   // editor: --total font-size 12px
    fontWeight: 'bold',
    color: COLOR_TEXT_PRIMARY,
  },
  totalValueFinal: {
    width: px(100),
    ...type(px(12)),
    fontWeight: 'bold',
    textAlign: 'right',
    color: COLOR_TEXT_PRIMARY,
  },

  // Bank details — the left half of the summary row
  bankSection: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,                       // editor: flex 1 1 auto; min-width 0
    paddingTop: px(2),                  // editor: padding-top 2px
    paddingRight: px(24),               // editor: the summary row's 24px gap
  },
  bankText: {
    ...type(px(10)),                   // editor: span font-size 10px
    marginBottom: px(2),                // editor: gap 2px
    color: COLOR_TEXT_PRIMARY,
  },
  // The editor uses `gap`, which adds nothing after the last line.
  bankTextLast: {
    ...type(px(10)),
    color: COLOR_TEXT_PRIMARY,
  },
  bankBold: {
    ...type(px(11)),                   // editor: strong font-size 11px
    fontWeight: 'bold',
  },

  footer: {
    position: 'absolute',
    bottom: mmToPt(PAGE_FOOTER_BOTTOM_MM),
    left: mmToPt(PAGE_MARGIN_LEFT_MM),
    right: mmToPt(PAGE_MARGIN_RIGHT_MM),
    textAlign: 'center',
  },
  footerText: {
    ...type(px(8)),                    // editor: font-size 8px
    color: COLOR_TEXT_PRIMARY,
  },
  footerPageNumber: {
    ...type(px(8)),
    marginTop: px(2),
    color: COLOR_TEXT_SECONDARY,
  },
});

interface InvoicePDFProps {
  data: InvoiceDetailData;
  /**
   * The page split the editor computed and is showing on screen. Rendering it
   * verbatim is what makes the export break where the preview says it will.
   * Without it the whole invoice is laid out on one flowing page, as before.
   */
  pages?: InvoicePagePlan[];
  /**
   * Overrides the document font. Only used to retry with a built-in family
   * when the Tinos faces cannot be fetched: the metrics no longer match the
   * preview exactly, but the user gets their invoice.
   */
  fontFamily?: string;
}

const InvoicePDF: React.FC<InvoicePDFProps> = ({ data, pages, fontFamily }) => {
  const { invoice, line_items, project, client, contact } = data;
  const totals = computeLineItemTotals(line_items);

  const stageDesc = data.stage?.stage_description;
  const fullProjectDesc = stageDesc
    ? `${project?.description || ''} – ${stageDesc}`
    : project?.description || '';

  const fullSiteAddress = formatSiteAddress(project);
  const showSiteAddress = isProgressClaimProjectType(project?.types);

  // Client billing address: structured by address_type (street / PO box / email),
  // with optional header lines above the company name and a "c/-" care-of line
  // (invoice override → stage → project) below it.
  const careOf = resolveCareOf(invoice, data.stage, project);
  const clientAddress = client ? buildClientAddressLines(client, careOf) : null;

  // Sections of the invoice. The page plan below places them, so the export
  // breaks exactly where the editor showed the break.
  const headSection = (
    <>
        {/* Header band: logo + TAX INVOICE + accent line */}
        <View style={styles.headerBand}>
          <Image src="/terpkosLogo.png" style={styles.logo} />
          <Text style={styles.headerTitle}>TAX INVOICE</Text>
          <View style={styles.headerAccent} />
        </View>

        {/* Two-column: Client left, invoice details right */}
        <View style={styles.metaSection}>
          <View style={styles.metaLeft}>
            {clientAddress && (
              <>
                {clientAddress.headerLines.map((line, i) => (
                  <Text key={`hdr-${i}`} style={styles.clientHeaderLine}>{line}</Text>
                ))}
                <Text style={styles.clientName}>{clientAddress.topName}</Text>
                {clientAddress.careOfClientName && (
                  <Text style={styles.clientCareOf}>c/- {clientAddress.careOfClientName}</Text>
                )}
                {clientAddress.addressLines.map((line, i) => (
                  <Text key={`addr-${i}`} style={styles.clientAddress}>{line}</Text>
                ))}
                {clientAddress.cityLine && (
                  <Text style={styles.clientCityLine}>{clientAddress.cityLine}</Text>
                )}
              </>
            )}
          </View>
          <View style={styles.metaRight}>
            <View style={styles.metaRow}>
              <Text style={styles.metaLabel}>Invoice No:</Text>
              <Text style={styles.metaValue}>{invoice.invoice_number}</Text>
            </View>
            <View style={styles.metaRow}>
              <Text style={styles.metaLabel}>Date:</Text>
              <Text style={styles.metaValue}>{formatInvoiceDate(invoice.invoice_date)}</Text>
            </View>
            {project && (
              <View style={styles.metaRow}>
                <Text style={styles.metaLabel}>Job No:</Text>
                <Text style={styles.metaValue}>{project.job_number}{data.stage ? `.${data.stage.stage_id}` : ''}</Text>
              </View>
            )}
          </View>
        </View>

        {/* ATTENTION */}
        {(invoice.contact_override || contact) && (
          <View style={styles.attentionRow}>
            <Text style={styles.attentionLabel}>ATTENTION:</Text>
            <Text>
              {invoice.contact_override
                ? invoice.contact_override
                : `${contact!.title ? contact!.title + ' ' : ''}${contact!.first_name} ${contact!.last_name}`}
            </Text>
          </View>
        )}

        {/* PROJECT: two blank lines below ATTENTION, as in the letter template */}
        {(invoice.project_description_override || project) && (
          <View style={styles.projectRow}>
            <Text style={styles.projectLabel}>PROJECT:</Text>
            <Text style={styles.projectContent}>{invoice.project_description_override || fullProjectDesc}</Text>
          </View>
        )}

        {showSiteAddress && (invoice.site_address_override || fullSiteAddress) && (
          <Text style={styles.addressRow}>{invoice.site_address_override || fullSiteAddress}</Text>
        )}

        {/* Service description */}
        {/*
          Both rows always occupy a line, because the editor always shows an
          editable row for them - with a grey placeholder when empty. Skipping
          the line here would print everything below it one line higher than the
          preview showed, which is exactly the drift these two surfaces exist to
          avoid. The placeholder text itself is an editing affordance and is not
          printed, so an unset field prints as the blank line the preview shows.
        */}
        <Text style={styles.serviceDesc}>{invoice.service_description || ' '}</Text>
        <Text style={styles.poRow}>{invoice.po_number ? `PO: ${invoice.po_number}` : ' '}</Text>

        {/* Claim Title */}
        {invoice.claim_title && (
          <Text style={styles.claimTitle}>{invoice.claim_title}</Text>
        )}

    </>
  );

  const tableHeader = (
      <View style={styles.tableHeader}>
        <Text style={[styles.tableHeaderCell, styles.colDescription]}>Description</Text>
        <Text style={[styles.tableHeaderCell, styles.colQty]}>Qty</Text>
        <Text style={[styles.tableHeaderCell, styles.colRate]}>Rate</Text>
        <Text style={[styles.tableHeaderCell, styles.colAmount]}>Amount</Text>
      </View>
  );

  const summarySection = (
      <View style={styles.summarySection}>
        <View style={styles.bankSection}>
          <Text style={styles.bankText}>Please pay <Text style={styles.bankBold}>TERPKOS ENGINEERING</Text> on invoice – no statement issued</Text>
          <Text style={styles.bankText}>E.F.T. your payment to Commonwealth Bank Australia</Text>
          <Text style={styles.bankText}>BSB: 066 000</Text>
          <Text style={styles.bankText}>Account No: 1100 0312</Text>
          <Text style={styles.bankTextLast}>Reference: {invoice.invoice_number}</Text>
        </View>
        <View style={styles.totalsColumn}>
          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>Our Fee (ex GST):</Text>
            <Text style={styles.totalsValue}>{formatCurrency(totals.ourFee)}</Text>
          </View>
          {totals.disbursements > 0 && (
            <>
              <View style={styles.totalsRow}>
                <Text style={styles.totalsLabel}>Disbursements (ex GST):</Text>
                <Text style={styles.totalsValue}>{formatCurrency(totals.disbursements)}</Text>
              </View>
              <View style={styles.totalsRow}>
                <Text style={styles.totalsLabel}>Subtotal:</Text>
                <Text style={styles.totalsValue}>{formatCurrency(totals.subtotal)}</Text>
              </View>
            </>
          )}
          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>GST (10%):</Text>
            <Text style={styles.totalsValue}>{formatCurrency(totals.gst)}</Text>
          </View>
          <View style={styles.totalRowFinal}>
            <Text style={styles.totalLabelFinal}>TOTAL (inc GST):</Text>
            <Text style={styles.totalValueFinal}>{formatCurrency(totals.total)}</Text>
          </View>
        </View>
      </View>
  );

  // Without a plan from the editor, fall back to one flowing page.
  const pagePlans: InvoicePagePlan[] = pages && pages.length > 0
    ? pages
    : [{ rows: line_items.map((_, i) => i), showSummary: true }];

  return (
    <PDFDocument>
      {pagePlans.map((page, pageIndex) => (
        // wrap={false} keeps react-pdf from re-paginating the plan; the
        // fixed-height box inside keeps every page exactly A4.
        <Page
          key={pageIndex}
          size="A4"
          style={fontFamily ? [styles.page, { fontFamily }] : styles.page}
          wrap={false}
        >
          <View style={styles.pageBox}>
            {pageIndex === 0 && headSection}

            {page.rows.length > 0 && (
              <>
                {tableHeader}
                {page.rows.map((rowIndex, positionOnPage) => {
                  const item = line_items[rowIndex];
                  if (!item) return null;
                  // The first line on a page never opens with a blank line,
                  // matching the editor's :first-child rule.
                  const gapAbove =
                    positionOnPage > 0 && hasGapBetween(line_items[rowIndex - 1], item);
                  return <LineItemRow key={rowIndex} item={item} gapAbove={gapAbove} />;
                })}
              </>
            )}

            {page.showSummary && summarySection}
          </View>

          <View style={styles.footer} fixed>
            <Text style={styles.footerText}>
              Terpkos Engineering Pty Ltd ACN 126 167 324 as trustee for the TEG Trust ABN 76 383 316 132
            </Text>
            {pagePlans.length > 1 && (
              <Text style={styles.footerPageNumber}>Page {pageIndex + 1} of {pagePlans.length}</Text>
            )}
          </View>
        </Page>
      ))}
    </PDFDocument>
  );
};

const LineItemRow: React.FC<{ item: InvoiceLineItem; gapAbove?: boolean }> = ({ item, gapAbove }) => {
  const indentPt = px((item.indent_level ?? 0) * LINE_ITEM_INDENT_PX); // = the editor's indent
  const rowStyle = gapAbove ? styles.tableRowGapAbove : styles.tableRow;

  // One blank line, matching the editor's $line-item-line. It takes the shared
  // gap like any other line: the editor's spacer row is a <tr> and picks up
  // --gap-above the same way, so skipping it here lost a line in the export.
  if (item.line_type === 'spacer') {
    return <View style={{ height: px(LINE_ITEM_LINE_PX), marginTop: gapAbove ? px(LINE_ITEM_LINE_PX) : 0 }} />;
  }

  if (item.line_type === 'header') {
    return (
      <View style={rowStyle}>
        <Text style={[styles.cellText, styles.colDescription, { fontWeight: 'bold', paddingLeft: indentPt }]}>{item.description}</Text>
        <Text style={styles.colQty} />
        <Text style={styles.colRate} />
        <Text style={styles.colAmount} />
      </View>
    );
  }

  if (item.line_type === 'text' || item.line_type === 'phase_label') {
    return (
      <View style={rowStyle}>
        <Text style={[styles.textLine, styles.colDescription, { paddingLeft: indentPt }]}>{item.description}</Text>
        <Text style={styles.colQty} />
        <Text style={styles.colRate} />
        <Text style={styles.colAmount} />
      </View>
    );
  }

  if (item.line_type === 'drawing') {
    return (
      <View style={rowStyle}>
        <View style={[styles.colDescription, styles.bulletRow, indentPt > 0 ? { paddingLeft: indentPt } : {}]}>
          <Text style={styles.drawingMark}>-</Text>
          {/* The column keeps its 28mm; the text inside it is given room to
              run on, so react-pdf never tries to break it (see
              drawingNumberText). */}
          <View style={styles.drawingNumber}>
            <Text style={styles.drawingNumberText}>{item.drawing_number ?? ''}</Text>
          </View>
          {/* "Issue" is printed even with no issue, as the editor shows its
              label: the columns must not shift between rows. */}
          <View style={styles.drawingIssue}>
            <Text style={styles.drawingIssueLabel}>Issue</Text>
            <Text style={styles.drawingIssueValue}>{item.issue_number ?? ''}</Text>
          </View>
          <Text style={styles.drawingTitle}>{item.description}</Text>
        </View>
        <Text style={styles.colQty} />
        <Text style={styles.colRate} />
        <Text style={styles.colAmount} />
      </View>
    );
  }

  if (item.line_type === 'bullet') {
    return (
      <View style={rowStyle}>
        <View style={[styles.colDescription, styles.bulletRow, indentPt > 0 ? { paddingLeft: indentPt } : {}]}>
          <Text style={styles.bulletMark}>-</Text>
          <Text style={styles.bulletText}>{item.description}</Text>
        </View>
        <Text style={styles.colQty} />
        <Text style={styles.colRate} />
        <Text style={styles.colAmount} />
      </View>
    );
  }

  const isDeduction = item.line_type === 'deduction' || item.line_type === 'prior_claims_deduction';
  const isLessClaims =
    item.line_type === 'prior_claims_deduction' ||
    (item.line_type === 'deduction' && item.description.trim() === 'Less previous claims');
  const textStyle = isDeduction && !isLessClaims ? styles.deductionText : styles.cellText;
  const numberStyle = isDeduction && !isLessClaims ? styles.deductionAmount : styles.cellNumber;

  return (
    <View style={rowStyle}>
      <Text style={[textStyle, styles.colDescription, indentPt > 0 ? { paddingLeft: indentPt } : {}]}>{item.description}</Text>
      <Text style={[numberStyle, styles.colQty]}>
        {isLessClaims ? '' : (item.quantity != null ? item.quantity.toString() : '')}
      </Text>
      <Text style={[numberStyle, styles.colRate]}>
        {isLessClaims ? '' : (item.rate != null ? formatCurrency(item.rate) : '')}
      </Text>
      <Text style={[numberStyle, styles.colAmount]}>
        {item.amount != null ? (
          isLessClaims
            ? `-(${formatCurrency(Math.abs(item.amount))})`
            : isDeduction
              ? `(${formatCurrency(Math.abs(item.amount))})`
              : formatCurrency(item.amount)
        ) : ''}
      </Text>
    </View>
  );
};




export default InvoicePDF;
